import { priceAtSupply } from '@/lib/bondingCurve'

export interface PricePoint {
  t: number
  supply: number
  price: number
  phase?: 'curve' | 'pool'
}

/** Deterministic seeded PRNG so each project's chart looks stable across renders. */
function seededRandom(seed: string): () => number {
  let h = 1779033703 ^ seed.length
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507)
    h = Math.imul(h ^ (h >>> 13), 3266489909)
    h ^= h >>> 16
    return (h >>> 0) / 4294967296
  }
}

/**
 * Synthesizes a plausible bonding-curve trade history ending exactly at the
 * project's current supply/price, so "quick launch" cards and detail pages
 * can show a pump.fun-style live chart without a real trade backend.
 */
export function generateCurveHistory(
  seed: string,
  basePrice: number,
  increment: number,
  currentSupply: number,
  pointCount = 20,
): PricePoint[] {
  const rand = seededRandom(seed)
  const target = Math.max(0, currentSupply)
  const points: PricePoint[] = []

  for (let i = 0; i <= pointCount; i++) {
    const progress = i / pointCount
    const wobble = i === pointCount ? 0 : (rand() - 0.5) * 0.08 * (1 - Math.abs(progress - 0.5) * 1.4)
    const eased = Math.min(1, Math.max(0, progress + wobble))
    const supplyAtPoint = target * eased
    points.push({
      t: i,
      supply: supplyAtPoint,
      price: i === 0 ? 0 : priceAtSupply(basePrice, increment, supplyAtPoint),
    })
  }

  return points
}
